import type { OnsiteWorkspace } from "./types";

const apiBase = import.meta.env.VITE_API_URL ?? "";

/** `facts.step0`, as the platform's setup callback records it: the last setup attempt. */
export interface Step0Summary {
  ranAt: string;
  ranBy: string | null;
  consentedAs: string | null;
  status: string;
  failedStep: string | null;
  message: string | null;
  steps: { step: string; status: string; detail: string }[];
}

export type SetupOutcome = "ok" | "failed";

// Mirrors STEP0_PAUSED_POLL in apps/platform/src/features/onsite/step0Plan.ts.
const DAILY_POLL_SCHEDULE = "0 9 * * *";

/** What to say when the agent's recorded schedule and the client's status call for it. */
type PollNotice = "paused" | "slow" | "restore";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const text = (value: unknown): string | null => (typeof value === "string" ? value : null);

/** A full-page navigation: the platform route redirects on to Google's consent screen. */
export function setupStartUrl(workspaceId: string): string {
  return `${apiBase}/auth/onsite/setup/start?${new URLSearchParams({ workspaceId }).toString()}`;
}

/** The last setup attempt, or null when there is none (or it is not well-formed). */
export function step0Summary(workspace: Pick<OnsiteWorkspace, "facts">): Step0Summary | null {
  const step0 = isRecord(workspace.facts) ? workspace.facts.step0 : undefined;
  if (!isRecord(step0)) return null;
  const ranAt = text(step0.ranAt);
  const status = text(step0.status);
  if (!ranAt || !status) return null;
  const steps = Array.isArray(step0.steps) ? step0.steps.filter(isRecord) : [];
  return {
    ranAt,
    ranBy: text(step0.ranBy),
    consentedAs: text(step0.consentedAs),
    status,
    failedStep: text(step0.failedStep),
    message: text(step0.message),
    steps: steps.map((s) => ({
      step: text(s.step) ?? "?",
      status: text(s.status) ?? "?",
      detail: text(s.detail) ?? "",
    })),
  };
}

export function setupOutcome(param: string | null): SetupOutcome | null {
  return param === "ok" || param === "failed" ? param : null;
}

/** Paused and offboarded clients' agents check in once a day, from their next setup. */
export function isPaused(workspace: Pick<OnsiteWorkspace, "projectStatus">): boolean {
  return workspace.projectStatus !== "active";
}

export function pollNotice(
  workspace: Pick<OnsiteWorkspace, "projectStatus" | "pollSchedule">,
): PollNotice | null {
  const daily = workspace.pollSchedule === DAILY_POLL_SCHEDULE;
  if (isPaused(workspace)) return daily ? "paused" : "slow";
  return daily ? "restore" : null;
}
